import { IconInfoCircle } from '@tabler/icons-react';
import {
  EmployeeDto,
  EmployeePaymentMethodStatus,
  OnboardingPeriodDto,
} from '@zorro/clients';
import { APP_ROUTES } from '@zorro/common-utils';
import { Link } from '@zorro/design-system/v2/Link';
import { Select, type SelectProps } from '@zorro/design-system/v2/Select';
import { Title } from '@zorro/design-system/v2/Title';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Box, Group, Icon, Stack } from '@zorro/web/design-system';
import { useMonolithMutation, useMonolithQuery } from '@zorro/web/ui-utils';

type ManagePaymentMethodStatusProps = {
  employeeId: EmployeeDto['id'];
  onboardingPeriodId: OnboardingPeriodDto['id'];
};

const ManagePaymentMethodStatus = ({
  employeeId,
  onboardingPeriodId,
}: ManagePaymentMethodStatusProps) => {
  const { data: paymentMethod, isLoading: isLoadingPaymentMethod } =
    useMonolithQuery({
      method: 'paymentsControllerGetPaymentMethod',
      params: [employeeId, onboardingPeriodId],
    });

  const { data: employee, isLoading: isLoadingEmployee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const {
    mutate: updatePaymentMethodStatus,
    isPending: isUpdatingPaymentMethodStatus,
  } = useMonolithMutation({
    method: 'paymentsControllerUpdatePaymentMethod',
    shouldInvalidateQueries: true,
  });

  const isLoading = isLoadingPaymentMethod || isLoadingEmployee;

  if (isLoading || !paymentMethod || !employee) {
    return null;
  }

  const { status } = paymentMethod;
  const { employerId } = employee;

  const handlePaymentMethodStatusChange: SelectProps['onChange'] = async (
    value
  ) => {
    if (!value) {
      return;
    }

    await updatePaymentMethodStatus([
      employeeId,
      {
        onboardingPeriodId,
        status: value as EmployeePaymentMethodStatus,
      },
    ]);
  };

  const generatePaymentMethodUrl =
    APP_ROUTES.OPS_CARRIER_APPLICATION_PAYMENT_METHOD({
      employerId,
      employeeId,
      onboardingPeriodId,
    });

  return (
    <Stack>
      <Box>
        <Tooltip
          inline
          label="Changing the status does not automatically affect the payment card/account in ECHO"
        >
          <Group gap="0.250rem" display="inline-flex">
            <Title order={3} size="sm">
              Change status
            </Title>
            <Icon icon={IconInfoCircle} size="1rem" />
          </Group>
        </Tooltip>
      </Box>
      <Group>
        <Select
          disabled={isUpdatingPaymentMethodStatus}
          value={status}
          data={[
            { value: 'EMPTY', label: 'No payment method' },
            { value: 'VALID', label: 'Valid' },
            { value: 'INVALID', label: 'Invalid (in-handling)' },
          ]}
          onChange={handlePaymentMethodStatusChange}
          maw="20rem"
        />
        {status === EmployeePaymentMethodStatus.EMPTY && (
          <Link href={generatePaymentMethodUrl} target="_blank">
            Generate payment method
          </Link>
        )}
      </Group>
    </Stack>
  );
};

export { ManagePaymentMethodStatus };
